import { useEffect, useState } from "react";
import { Modal, message } from "antd";
import { MenuSource, MenuSourceTile } from "./MenuSourceTile";
import { AssignSourceModal, DesignOption } from "./AssignSourceModal";
import YextApi, { YextMenuTile, yextErrorMessage } from "../../api/YextApi";
import { env } from "../../env/env";
import "./ListingsHomeTab.scss";

function menuThumbUrl(designId: string): string {
  return `${env.rendererPath}/design/thumbnail/${designId}?height=${env.rendererImageHeight}`;
}

function formatPublishedDate(
  value: YextMenuTile["publishedDate"]
): string | undefined {
  if (value == null) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function toMenuSource(t: YextMenuTile): MenuSource {
  return {
    id: String(t.id),
    designId: t.designId,
    name: t.name,
    alias: t.alias || undefined,
    thumbUrl: menuThumbUrl(t.designId),
    publishedAt: formatPublishedDate(t.publishedDate),
    connected: t.status !== "not_live",
  };
}

interface Props {
  locationId?: number;
}

export function ListingsHomeTab({ locationId }: Props) {
  const [menus, setMenus] = useState<MenuSource[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getMenus(locationId).then(({ data }) =>
      setMenus(data.map(toMenuSource))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const editingMenu = menus.find((m) => m.id === editingId);

  const openAssign = (id: string | null) => {
    setEditingId(id);
    setAssignOpen(true);
  };

  const closeAssign = () => {
    setAssignOpen(false);
    setEditingId(null);
  };

  const replaceMenu = (menu: MenuSource) =>
    setMenus((prev) => prev.map((m) => (m.id === menu.id ? menu : m)));

  const publishMenu = async (id: string) => {
    if (!locationId || publishingId) return;
    setPublishingId(id);
    try {
      const { data } = await YextApi.publishMenu(locationId, Number(id));
      replaceMenu(toMenuSource(data));
      message.success("Menu published to your listing.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to publish menu."));
    } finally {
      setPublishingId(null);
    }
  };

  const handleSetSource = async (design: DesignOption, alias: string) => {
    if (!locationId) {
      message.error("No location selected.");
      closeAssign();
      return;
    }
    const req = { designId: design.id, name: design.name, alias };
    const targetId = editingId;
    closeAssign();
    let saved: MenuSource;
    try {
      if (targetId === null) {
        const { data } = await YextApi.createMenu(locationId, req);
        saved = toMenuSource(data);
        setMenus((prev) => [...prev, saved]);
      } else {
        const { data } = await YextApi.updateMenu(
          locationId,
          Number(targetId),
          req
        );
        saved = toMenuSource(data);
        replaceMenu(saved);
      }
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save menu."));
      return;
    }
    await publishMenu(saved.id);
  };

  const removeMenu = (menu: MenuSource) => {
    if (!locationId) return;
    Modal.confirm({
      title: "Remove this menu?",
      content: `"${menu.alias || menu.name}" will be removed from your listing.`,
      okText: "Remove Menu",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await YextApi.deleteMenu(locationId, Number(menu.id));
          setMenus((prev) => prev.filter((m) => m.id !== menu.id));
        } catch (e) {
          message.error(yextErrorMessage(e, "Failed to remove menu."));
          return Promise.reject();
        }
      },
    });
  };

  return (
    <div className="listings-home">
      <div className="menus-list">
        {menus.map((menu) => (
          <MenuSourceTile
            key={menu.id}
            menu={menu}
            publishing={publishingId === menu.id}
            onOpenDesign={() => {
              window.open(
                `${env.timberContextPath}/design?id=${menu.designId}`,
                "_blank",
                "noopener,noreferrer"
              );
            }}
            onEdit={() => openAssign(menu.id)}
            onDelete={() => removeMenu(menu)}
            onConnect={() => publishMenu(menu.id)}
          />
        ))}

        <button
          type="button"
          className="menus-add-btn"
          onClick={() => openAssign(null)}
        >
          + Add menu
        </button>
      </div>

      <AssignSourceModal
        open={assignOpen}
        locationId={locationId}
        initialDesignId={editingMenu?.designId}
        initialAlias={editingMenu?.alias}
        onClose={closeAssign}
        onSetSource={handleSetSource}
      />
    </div>
  );
}
